import {productCategoryAssignmentInteractiveSections} from "../corrective-interactions/product-category-assignment";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * @param {*} item
 * @returns {boolean}
 */
function isSeedRow(item) {
    return !!toPartNumberUpper(item?.partNumber) && !!item?.parentCategoryTitle?.trim();
}

/**
 * Product category assignment import — phased validation (see {@link BaseImportTransformer} run contract).
 *
 * 1) Empty CSV rows (no `item`) — domain placeholder issue
 * 2) CSV-only seed rule — trimmed part number + root category required (`MISSING_REQUIRED_FIELDS`)
 * 3) Batched part lookup (HTTP skipped when there are no seed part numbers)
 * 4) Unknown part for seed rows (`INVALID_PART`)
 *
 * Preview (`!isFinalRun`): placeholders on rows with issues; final (`isFinalRun`): `null` where blocked.
 * Payload keys match `Api.assignPartCategoryByTitle`.
 */
export class ProductCategoryAssignmentImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            codes: [],
        }));

        // ── Phase 1: preliminary (CSV-only) ─────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!isSeedRow(rs.item)) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        // ── Phase 2: batched part lookup (only seed rows contribute part numbers) ─
        const uniqueParts = [
            ...new Set(rowState.filter((r) => r.item && isSeedRow(r.item)).map((r) => r.partNoUpper).filter(Boolean)),
        ].sort();

        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        // ── Phase 3: post-lookup (unknown part on seed rows) ──────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Emit merged issues (one row -> one entry, deduped codes) ───────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));
        const bypassedIssuesByCorrectiveInteractions = productCategoryAssignmentInteractiveSections.flatMap(s => s.preventativeInteractionsInvalidityCodeBypass);

        // ── Build apiRequests (same length as `rows`) ───────────────────────────────
        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {partNumber: undefined, parentCategoryTitle: undefined};
            }

            const hasIssue = issueIndexSet.has(index);
            const partNoUpper = toPartNumberUpper(item.partNumber);
            const parent = item.parentCategoryTitle?.trim();
            const leaf = item.categoryTitle?.trim();

            if (hasIssue && isFinalRun) {
                // if one of the unique issues on this row is not bypassed by corrective interactions, block the API request by returning null
                const rowIssues = preventativeIssues.find(p => p.rowIndex === index)?.invalidityCodes ?? [];
                const hasNonBypassedIssue = rowIssues.some(code => !bypassedIssuesByCorrectiveInteractions.includes(code));
                if (hasNonBypassedIssue) {
                    return null;
                }
            }

            return {
                partNumber: partNoUpper,
                parentCategoryTitle: parent?.length ? parent : undefined,
                categoryTitle: leaf?.length ? leaf : undefined,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
